/**
 * Vendor's inbox of pending customer-initiated change requests. Approve / Deny
 * actions apply the change to the booking immediately (status='cancelled' for
 * cancel, new add_ons[] / insurance for the other two). Money settlement stays
 * off-platform — the customer receives an email either way.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import Money from "@/components/Money";

const KIND_LABEL = {
  cancel: "Cancellation",
  update_extras: "Extras change",
  update_insurance: "Insurance change",
};

function _fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function BookingChangeRequestsInbox({ onChanged }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);  // {request, decision}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/booking-changes/inbox");
      setRequests(data || []);
    } catch (e) {
      console.error("inbox load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post(`/booking-changes/${target.request.id}/decide`, {
        decision: target.decision,
        vendor_note: note.trim() || null,
      });
      toast.success(target.decision === "approved" ? "Request approved — customer notified" : "Request declined — customer notified");
      setTarget(null);
      setNote("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <>
      <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 sm:p-5 shadow-sm mb-6" data-testid="vendor-change-requests-inbox">
        <div className="flex items-center gap-2 mb-3">
          <Pencil className="h-4 w-4 text-amber-700" />
          <h2 className="font-display text-lg">Customer change requests</h2>
          <span className="text-xs text-neutral-500 ml-1">{requests.length} pending</span>
        </div>
        <div className="space-y-2">
          {requests.map((r) => {
            const delta = Number(r.delta_estimate_eur || 0);
            const deltaTone = delta > 0 ? "text-amber-700" : delta < 0 ? "text-emerald-700" : "text-neutral-500";
            return (
              <div key={r.id} className="rounded-xl bg-white border border-amber-100 p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`change-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="font-medium">{r.customer_name || r.customer_email}</span>
                    <span className="text-neutral-500"> wants </span>
                    <span className="font-medium text-amber-800">{KIND_LABEL[r.kind] || r.kind}</span>
                    {r.car_label && <span className="text-neutral-500"> on </span>}
                    {r.car_label && <span className="font-medium">{r.car_label}</span>}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    Submitted {_fmtAgo(r.created_at)}
                    {Math.abs(delta) >= 0.01 && (
                      <> · Est. total change: <span className={`font-semibold ${deltaTone}`}>{delta > 0 ? "+" : ""}<Money amount={delta} /></span></>
                    )}
                  </div>
                  {r.customer_note && (
                    <div className="text-xs italic text-neutral-600 mt-1">"{r.customer_note}"</div>
                  )}
                  {/* Per-kind diff */}
                  {r.kind === "update_extras" && (
                    <ProposalDiff
                      label="New extras"
                      currentItems={(r.current_snapshot?.add_ons || []).map((a) => a.name)}
                      proposedItems={(r.proposed?.add_ons || []).map((a) => a.name)}
                    />
                  )}
                  {r.kind === "update_insurance" && (
                    <div className="text-xs mt-1 text-neutral-600">
                      <span className="text-neutral-400">Current: </span>
                      <span>{r.current_snapshot?.insurance_plan_id ? "(plan)" : "Minimum coverage"}</span>
                      <span className="mx-1.5 text-neutral-400">→</span>
                      <span className="font-medium">{r.proposed?.insurance_plan_name || "Minimum coverage"}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => { setTarget({ request: r, decision: "denied" }); setNote(""); }} className="rounded-full text-xs h-7" data-testid={`deny-${r.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Deny
                  </Button>
                  <Button size="sm" onClick={() => { setTarget({ request: r, decision: "approved" }); setNote(""); }} className="rounded-full text-xs h-7 bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />Approve
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent data-testid="decide-dialog">
          <DialogHeader>
            <DialogTitle>{target?.decision === "approved" ? "Approve change request?" : "Decline change request?"}</DialogTitle>
            <DialogDescription>
              {target?.decision === "approved"
                ? "The booking will be updated immediately and the customer will be emailed. Money movement (refunds / extra charges) is handled separately."
                : "The customer's request stays declined and the original booking is unchanged. Add a short reason to help them understand."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note to the customer (max 500 chars)"
            maxLength={500}
            className="rounded-xl"
            data-testid="decide-note"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)} className="rounded-full" data-testid="decide-cancel">Cancel</Button>
            <Button
              onClick={decide}
              disabled={busy}
              className={`rounded-full ${target?.decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              data-testid="decide-confirm"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              {target?.decision === "approved" ? "Approve" : "Decline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProposalDiff({ label, currentItems, proposedItems }) {
  const currentSet = new Set(currentItems);
  const proposedSet = new Set(proposedItems);
  const added = proposedItems.filter((n) => !currentSet.has(n));
  const removed = currentItems.filter((n) => !proposedSet.has(n));
  if (!added.length && !removed.length) {
    return <div className="text-[11px] text-neutral-400 mt-1">No net change · " {label} "</div>;
  }
  return (
    <div className="text-[11px] mt-1 flex flex-wrap gap-1.5">
      {added.map((n) => (
        <span key={`a-${n}`} className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">+ {n}</span>
      ))}
      {removed.map((n) => (
        <span key={`r-${n}`} className="px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">− {n}</span>
      ))}
    </div>
  );
}
